'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useEffectEvent } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useToast } from '@/shared/providers';
import { ehUrlNavegavelSegura } from '@/shared/utils/urlSegura';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { ArquivoDoPacote, ChaveDaSecao, FormatoDownload, PortaDosAutos } from '../types/baixarProcesso.types';
import { abrirPacoteDoAviso, assinarArquivo, carregarRegistroDeAnexos, escolhaDeFormatoReaberta, formatoEscolhido, formatoMarcado, listarAutos, menuFechado, painelFechado, pedirPacote, recusaDefinitiva, registrarCienciaDosAnexos, secaoAlternada, selectArquivoEmPreparo, selectAvisoDaSelecao, selectCarregandoOpcoes, selectCarregandoRegistro, selectEnviandoCiencia, selectEtapaGeracao, selectErroGeracao, selectFalhaNoRegistro, selectFalhouPorLimite, selectFormatoDownload, selectFormatoEmEscolha, selectMenuBaixarAberto, selectOpcoesDosAutos, selectPacoteGerado, selectPacoteJaGeradoDaSelecao, selectPacoteReaproveitado, selectPainelBaixarAberto, selectRegistroDeAnexos, selectSecoesMarcadas, todasAsSecoesAlternadas, trocaDeFormatoPedida, voltouParaSelecao } from '../store';
import { precisaDoRegistro } from '../utils/anexosDoRegistro';
import { iniciarDownload, urlDoPacoteBaixavel } from '../utils/downloadDoPacote';
import { PARAMETRO_PDF_DO_AVISO, urlSemPdfDoAviso } from '../utils/rotaDoPdfDoAviso';
import { useEsperaDoPacote } from './useEsperaDoPacote';
interface UsePainelBaixarProcessoInput {
  porta: PortaDosAutos;
  processoId: string;
}
export function usePainelBaixarProcesso(t0) {
  const $ = _c(133);
  const {
    porta,
    processoId
  } = t0;
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const parametros = useSearchParams();
  const toast = useToast();
  let t1;
  if ($[0] !== parametros || $[1] !== porta) {
    t1 = porta === "escritorio" ? parametros.get(PARAMETRO_PDF_DO_AVISO) : null;
    $[0] = parametros;
    $[1] = porta;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const pacoteDoAviso = t1;
  const menuAberto = useAppSelector(selectMenuBaixarAberto);
  const painelAberto = useAppSelector(selectPainelBaixarAberto);
  const formato = useAppSelector(selectFormatoDownload);
  const formatoEmEscolha = useAppSelector(selectFormatoEmEscolha);
  const secoesMarcadas = useAppSelector(selectSecoesMarcadas);
  const etapa = useAppSelector(selectEtapaGeracao);
  const pacote = useAppSelector(selectPacoteGerado);
  const erroGeracao = useAppSelector(selectErroGeracao);
  const falhouPorLimite = useAppSelector(selectFalhouPorLimite);
  const reaproveitado = useAppSelector(selectPacoteReaproveitado);
  const opcoes = useAppSelector(selectOpcoesDosAutos);
  const carregandoOpcoes = useAppSelector(selectCarregandoOpcoes);
  const avisoDaSelecao = useAppSelector(selectAvisoDaSelecao);
  const pacoteJaGerado = useAppSelector(selectPacoteJaGeradoDaSelecao);
  const arquivoEmPreparo = useAppSelector(selectArquivoEmPreparo);
  const registroDeAnexos = useAppSelector(selectRegistroDeAnexos);
  const carregandoRegistro = useAppSelector(selectCarregandoRegistro);
  const falhaNoRegistro = useAppSelector(selectFalhaNoRegistro);
  const enviandoCiencia = useAppSelector(selectEnviandoCiencia);
  const espera = useEsperaDoPacote();
  let t2;
  if ($[3] !== dispatch || $[4] !== porta || $[5] !== processoId) {
    t2 = () => void dispatch(listarAutos({
      porta,
      processoId
    }));
    $[3] = dispatch;
    $[4] = porta;
    $[5] = processoId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const carregarOpcoes = t2;
  let t3;
  if ($[7] !== carregarOpcoes || $[8] !== dispatch || $[9] !== porta || $[10] !== processoId) {
    t3 = secoes => {
      const pedido = dispatch(pedirPacote({
        porta,
        processoId,
        secoes
      }));
      pedido.then(acao => {
        if (pedirPacote.rejected.match(acao) && acao.payload?.motivo === "secoesIndisponiveis") {
          carregarOpcoes();
        }
      });
    };
    $[7] = carregarOpcoes;
    $[8] = dispatch;
    $[9] = porta;
    $[10] = processoId;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const pedir = t3;
  let t4;
  if ($[12] !== carregarOpcoes || $[13] !== dispatch || $[14] !== pedir) {
    t4 = escolhido => {
      dispatch(formatoEscolhido(escolhido));
      if (escolhido === "secoes") {
        carregarOpcoes();
        return;
      }
      pedir();
    };
    $[12] = carregarOpcoes;
    $[13] = dispatch;
    $[14] = pedir;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const escolherFormato = t4;
  let t5;
  if ($[16] !== formato || $[17] !== pedir || $[18] !== secoesMarcadas) {
    t5 = () => formato === "secoes" ? pedir(secoesMarcadas) : pedir();
    $[16] = formato;
    $[17] = pedir;
    $[18] = secoesMarcadas;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const gerar = t5;
  const pacotePronto = etapa === "pronto" ? pacote : null;
  let t6;
  if ($[20] !== pacotePronto || $[21] !== porta) {
    t6 = pacotePronto && precisaDoRegistro(pacotePronto, porta) ? pacotePronto.pacoteId : null;
    $[20] = pacotePronto;
    $[21] = porta;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const pacoteDoRegistro = t6;
  let t7;
  if ($[23] !== dispatch || $[24] !== porta || $[25] !== processoId) {
    t7 = pacoteId => dispatch(carregarRegistroDeAnexos({
      porta,
      processoId,
      pacoteId
    }));
    $[23] = dispatch;
    $[24] = porta;
    $[25] = processoId;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const buscarRegistro = t7;
  let t8;
  let t9;
  if ($[27] !== buscarRegistro || $[28] !== pacoteDoRegistro) {
    t8 = () => {
      if (!pacoteDoRegistro) {
        return;
      }
      const pedido_0 = buscarRegistro(pacoteDoRegistro);
      return () => pedido_0.abort();
    };
    t9 = [buscarRegistro, pacoteDoRegistro];
    $[27] = buscarRegistro;
    $[28] = pacoteDoRegistro;
    $[29] = t8;
    $[30] = t9;
  } else {
    t8 = $[29];
    t9 = $[30];
  }
  useEffect(t8, t9);
  let t10;
  if ($[31] !== dispatch || $[32] !== enviandoCiencia || $[33] !== pacote || $[34] !== porta || $[35] !== processoId || $[36] !== toast) {
    t10 = async () => {
      if (!pacote || enviandoCiencia) {
        return;
      }
      const resultado = await dispatch(registrarCienciaDosAnexos({
        porta,
        processoId,
        pacoteId: pacote.pacoteId
      }));
      if (registrarCienciaDosAnexos.rejected.match(resultado) && !recusaDefinitiva(resultado.payload)) {
        toast.showError("N\xE3o foi poss\xEDvel registrar a ci\xEAncia", resultado.payload?.mensagem ?? "Tente novamente em instantes.");
      }
    };
    $[31] = dispatch;
    $[32] = enviandoCiencia;
    $[33] = pacote;
    $[34] = porta;
    $[35] = processoId;
    $[36] = toast;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const darCiencia = t10;
  let t11;
  let t12;
  if ($[38] !== dispatch) {
    t11 = () => () => void dispatch(painelFechado());
    t12 = [dispatch];
    $[38] = dispatch;
    $[39] = t11;
    $[40] = t12;
  } else {
    t11 = $[39];
    t12 = $[40];
  }
  useEffect(t11, t12);
  let t13;
  if ($[41] !== dispatch || $[42] !== parametros || $[43] !== pathname || $[44] !== porta || $[45] !== processoId || $[46] !== router) {
    t13 = pacoteId_0 => {
      dispatch(formatoEscolhido("completo"));
      const pedido_1 = dispatch(abrirPacoteDoAviso({
        porta,
        processoId,
        pacoteId: pacoteId_0
      }));
      pedido_1.then(acao_0 => {
        if (abrirPacoteDoAviso.rejected.match(acao_0) && acao_0.meta.aborted) {
          return;
        }
        router.replace(urlSemPdfDoAviso(pathname, parametros), {
          scroll: false
        });
        const url = abrirPacoteDoAviso.fulfilled.match(acao_0) ? urlDoPacoteBaixavel(acao_0.payload.pacote) : null;
        if (url) {
          iniciarDownload(url);
        }
      });
      return pedido_1;
    };
    $[41] = dispatch;
    $[42] = parametros;
    $[43] = pathname;
    $[44] = porta;
    $[45] = processoId;
    $[46] = router;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  const abrirPdfDoAviso = useEffectEvent(t13);
  let t14;
  if ($[48] !== abrirPdfDoAviso || $[49] !== pacoteDoAviso) {
    t14 = () => {
      if (!pacoteDoAviso) {
        return;
      }
      const pedido_2 = abrirPdfDoAviso(pacoteDoAviso);
      return () => pedido_2.abort();
    };
    $[48] = abrirPdfDoAviso;
    $[49] = pacoteDoAviso;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== pacoteDoAviso) {
    t15 = [pacoteDoAviso];
    $[51] = pacoteDoAviso;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  useEffect(t14, t15);
  let t16;
  if ($[53] !== arquivoEmPreparo || $[54] !== dispatch || $[55] !== pacote || $[56] !== porta || $[57] !== processoId || $[58] !== toast) {
    t16 = async arquivo => {
      if (!pacote || arquivoEmPreparo) {
        return;
      }
      const resultado_0 = await dispatch(assinarArquivo({
        porta,
        processoId,
        pacoteId: pacote.pacoteId,
        arquivo
      }));
      if (assinarArquivo.fulfilled.match(resultado_0)) {
        if (ehUrlNavegavelSegura(resultado_0.payload.url)) {
          iniciarDownload(resultado_0.payload.url);
        } else {
          toast.showError("N\xE3o foi poss\xEDvel baixar o arquivo", "Tente novamente em instantes.");
        }
        return;
      }
      if (recusaDefinitiva(resultado_0.payload) || resultado_0.payload?.motivo === "cienciaPendente") {
        return;
      }
      toast.showError("N\xE3o foi poss\xEDvel baixar o arquivo", resultado_0.payload?.mensagem ?? "Tente novamente em instantes.");
    };
    $[53] = arquivoEmPreparo;
    $[54] = dispatch;
    $[55] = pacote;
    $[56] = porta;
    $[57] = processoId;
    $[58] = toast;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  const baixarArquivo = t16;
  const t17 = opcoes?.secoes ?? null;
  let t18;
  if ($[60] !== buscarRegistro || $[61] !== pacoteDoRegistro) {
    t18 = () => {
      if (pacoteDoRegistro) {
        buscarRegistro(pacoteDoRegistro);
      }
    };
    $[60] = buscarRegistro;
    $[61] = pacoteDoRegistro;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  let t19;
  if ($[63] !== darCiencia) {
    t19 = () => void darCiencia();
    $[63] = darCiencia;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] !== dispatch) {
    t20 = () => dispatch(menuFechado());
    $[65] = dispatch;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  const t21 = t20;
  let t22;
  if ($[67] !== dispatch) {
    t22 = escolhido_0 => dispatch(formatoMarcado(escolhido_0));
    $[67] = dispatch;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  const t23 = t22;
  let t24;
  if ($[69] !== escolherFormato || $[70] !== formatoEmEscolha) {
    t24 = () => {
      if (formatoEmEscolha) {
        escolherFormato(formatoEmEscolha);
      }
    };
    $[69] = escolherFormato;
    $[70] = formatoEmEscolha;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== dispatch || $[73] !== porta) {
    t25 = () => dispatch(porta === "escritorio" ? trocaDeFormatoPedida() : escolhaDeFormatoReaberta());
    $[72] = dispatch;
    $[73] = porta;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  const t26 = t25;
  let t27;
  if ($[75] !== dispatch || $[76] !== espera) {
    t27 = () => {
      espera.avisarSeEmEspera();
      dispatch(painelFechado());
    };
    $[75] = dispatch;
    $[76] = espera;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== dispatch) {
    t28 = chave => dispatch(secaoAlternada(chave));
    $[78] = dispatch;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  const t29 = t28;
  let t30;
  if ($[80] !== dispatch) {
    t30 = () => dispatch(todasAsSecoesAlternadas());
    $[80] = dispatch;
    $[81] = t30;
  } else {
    t30 = $[81];
  }
  const t31 = t30;
  let t32;
  if ($[82] !== dispatch) {
    t32 = () => dispatch(voltouParaSelecao());
    $[82] = dispatch;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  const t33 = t32;
  let t34;
  if ($[84] !== dispatch || $[85] !== formato || $[86] !== porta) {
    t34 = () => {
      if (formato === "secoes") {
        dispatch(voltouParaSelecao());
      } else {
        if (porta === "secretaria") {
          dispatch(escolhaDeFormatoReaberta());
        } else {
          dispatch(painelFechado());
        }
      }
    };
    $[84] = dispatch;
    $[85] = formato;
    $[86] = porta;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== carregarOpcoes || $[89] !== formato || $[90] !== gerar || $[91] !== opcoes) {
    t35 = () => formato === "secoes" && !opcoes ? carregarOpcoes() : gerar();
    $[88] = carregarOpcoes;
    $[89] = formato;
    $[90] = gerar;
    $[91] = opcoes;
    $[92] = t35;
  } else {
    t35 = $[92];
  }
  let t36;
  if ($[93] !== escolherFormato) {
    t36 = () => escolherFormato("secoes");
    $[93] = escolherFormato;
    $[94] = t36;
  } else {
    t36 = $[94];
  }
  let t37;
  if ($[95] !== arquivoEmPreparo || $[96] !== avisoDaSelecao || $[97] !== baixarArquivo || $[98] !== carregandoOpcoes || $[99] !== carregandoRegistro || $[100] !== enviandoCiencia || $[101] !== erroGeracao || $[102] !== escolherFormato || $[103] !== espera.continuarEsperando || $[104] !== espera.segundosParaDecidir || $[105] !== etapa || $[106] !== falhaNoRegistro || $[107] !== falhouPorLimite || $[108] !== formato || $[109] !== formatoEmEscolha || $[110] !== gerar || $[111] !== menuAberto || $[112] !== pacote || $[113] !== pacoteJaGerado || $[114] !== painelAberto || $[115] !== reaproveitado || $[116] !== registroDeAnexos || $[117] !== secoesMarcadas || $[118] !== t17 || $[119] !== t18 || $[120] !== t19 || $[121] !== t21 || $[122] !== t23 || $[123] !== t24 || $[124] !== t26 || $[125] !== t27 || $[126] !== t29 || $[127] !== t31 || $[128] !== t33 || $[129] !== t34 || $[130] !== t35 || $[131] !== t36) {
    t37 = {
      menuAberto,
      painelAberto,
      formato,
      formatoEmEscolha,
      secoesMarcadas,
      etapa,
      pacote,
      reaproveitado,
      erroGeracao,
      falhouPorLimite,
      secoes: t17,
      carregandoSecoes: carregandoOpcoes,
      avisoDaSelecao,
      pacoteJaGerado,
      arquivoEmPreparo,
      registroDeAnexos,
      carregandoRegistro,
      falhaNoRegistro,
      enviandoCiencia,
      recarregarRegistro: t18,
      darCiencia: t19,
      segundosParaDecidir: espera.segundosParaDecidir,
      continuarEsperando: espera.continuarEsperando,
      fecharMenu: t21,
      escolherFormato,
      marcarFormato: t23,
      continuarComFormato: t24,
      trocarFormato: t26,
      fecharPainel: t27,
      alternarSecao: t29,
      alternarTodasAsSecoes: t31,
      voltarParaSelecao: t33,
      cancelarGeracao: t34,
      gerar,
      tentarNovamente: t35,
      escolherSecoes: t36,
      baixarArquivo
    };
    $[95] = arquivoEmPreparo;
    $[96] = avisoDaSelecao;
    $[97] = baixarArquivo;
    $[98] = carregandoOpcoes;
    $[99] = carregandoRegistro;
    $[100] = enviandoCiencia;
    $[101] = erroGeracao;
    $[102] = escolherFormato;
    $[103] = espera.continuarEsperando;
    $[104] = espera.segundosParaDecidir;
    $[105] = etapa;
    $[106] = falhaNoRegistro;
    $[107] = falhouPorLimite;
    $[108] = formato;
    $[109] = formatoEmEscolha;
    $[110] = gerar;
    $[111] = menuAberto;
    $[112] = pacote;
    $[113] = pacoteJaGerado;
    $[114] = painelAberto;
    $[115] = reaproveitado;
    $[116] = registroDeAnexos;
    $[117] = secoesMarcadas;
    $[118] = t17;
    $[119] = t18;
    $[120] = t19;
    $[121] = t21;
    $[122] = t23;
    $[123] = t24;
    $[124] = t26;
    $[125] = t27;
    $[126] = t29;
    $[127] = t31;
    $[128] = t33;
    $[129] = t34;
    $[130] = t35;
    $[131] = t36;
    $[132] = t37;
  } else {
    t37 = $[132];
  }
  return t37;
}